import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotificationService } from '../services';
import { isNetworkError, NETWORK_ERROR_MESSAGES } from '../utils';

/**
 * Reference to the currently visible network-error snackbar. Module-scoped so
 * every interceptor invocation (one per HTTP request) shares the same gate.
 * While the snackbar is open we suppress new ones — that handles the burst of
 * parallel requests that all fail at once. Once the user dismisses it (or it
 * auto-dismisses), the ref clears, so the very next failing request gets a
 * fresh toast immediately (no arbitrary time window).
 */
let activeNetworkSnackbar: MatSnackBarRef<unknown> | null = null;

/**
 * Global resolver for transport-level HTTP failures (`status: 0`). Replaces
 * Angular's cryptic "Http failure response for <url>: 0 Unknown Error" with a
 * single, friendly toast — distinguishing "browser is offline" from "server
 * unreachable" via `navigator.onLine`. The original `HttpErrorResponse` is
 * still re-thrown so downstream stores / components can clear loading state
 * and fall through their own (unrelated) error UI.
 *
 * Ordering: register this interceptor BEFORE `serverErrorInterceptor` (and
 * any feature-specific interceptor that swallows errors) so it always gets a
 * chance to surface the network notification first.
 */
export const networkErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const notificationService = inject(NotificationService);

  return next(req).pipe(
    catchError((err: unknown): Observable<HttpEvent<unknown>> => {
      if (isNetworkError(err) && activeNetworkSnackbar === null) {
        const msg =
          typeof navigator !== 'undefined' && navigator.onLine === false
            ? NETWORK_ERROR_MESSAGES.offline
            : NETWORK_ERROR_MESSAGES.unreachable;
        activeNetworkSnackbar = notificationService.showError(msg, 5000);
        activeNetworkSnackbar.afterDismissed().subscribe((): void => {
          activeNetworkSnackbar = null;
        });
      }
      return throwError((): unknown => (err instanceof HttpErrorResponse ? err : err));
    }),
  );
};
